import { useState, useEffect } from 'react';
import { X, Save, Calendar, FileText, Hash, Percent } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';

export default function EditSyllabusItemModal({ item, itemIndex, courseId, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    due_date: '',
    description: '',
    item_type: 'assignment',
    point_value: '',
    weight_percentage: ''
  });
  
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      // Pre-fill form with existing data
      setFormData({
        title: item.title || '',
        due_date: item.due_date && moment(item.due_date).isValid() 
          ? moment(item.due_date).format('YYYY-MM-DD') 
          : '',
        description: item.description || '',
        item_type: item.item_type || 'assignment',
        point_value: item.point_value || '',
        weight_percentage: item.weight_percentage || ''
      });
    }
  }, [item]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (formData.due_date && !moment(formData.due_date, 'YYYY-MM-DD', true).isValid()) {
      newErrors.due_date = 'Invalid date format';
    }
    
    if (formData.weight_percentage && (
      isNaN(formData.weight_percentage) || 
      formData.weight_percentage < 0 || 
      formData.weight_percentage > 100
    )) {
      newErrors.weight_percentage = 'Must be between 0 and 100';
    }
    
    if (formData.point_value && isNaN(formData.point_value)) {
      newErrors.point_value = 'Must be a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    setSaving(true);

    try {
      // Call the onSave callback with updated data
      await onSave(itemIndex, {
        ...formData,
        point_value: formData.point_value ? parseFloat(formData.point_value) : null,
        weight_percentage: formData.weight_percentage ? parseFloat(formData.weight_percentage) : null
      });
      
      toast.success('✓ Task updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white max-w-2xl w-full rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#E8934D] to-[#E85D4D] text-white p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Edit Task
          </h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-[#E8934D] ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Case Study Quiz #3"
              disabled={saving}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Due Date
            </label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => handleChange('due_date', e.target.value)}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-[#E8934D] ${
                errors.due_date ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={saving}
            />
            {errors.due_date && (
              <p className="text-red-500 text-sm mt-1">{errors.due_date}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              ℹ️ Adding a date will make this item appear on your calendar
            </p>
          </div>

          {/* Item Type */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Type *
            </label>
            <select
              value={formData.item_type}
              onChange={(e) => handleChange('item_type', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#E8934D]"
              disabled={saving}
            >
              <option value="assignment">Assignment</option>
              <option value="exam">Exam</option>
              <option value="project">Project</option>
              <option value="quiz">Quiz</option>
              <option value="reading">Reading</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows="4"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#E8934D] resize-vertical"
              placeholder="Additional details about this task..."
              disabled={saving}
            />
          </div>

          {/* Point Value and Weight Percentage */}
          <div className="grid grid-cols-2 gap-4">
            {/* Point Value */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Hash className="w-4 h-4" />
                Point Value
              </label>
              <input
                type="number"
                value={formData.point_value}
                onChange={(e) => handleChange('point_value', e.target.value)}
                min="0"
                step="1"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-[#E8934D] ${
                  errors.point_value ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 100"
                disabled={saving}
              />
              {errors.point_value && (
                <p className="text-red-500 text-sm mt-1">{errors.point_value}</p>
              )}
            </div>

            {/* Weight Percentage */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Weight (%)
              </label>
              <input
                type="number"
                value={formData.weight_percentage}
                onChange={(e) => handleChange('weight_percentage', e.target.value)}
                min="0"
                max="100"
                step="0.1"
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-[#E8934D] ${
                  errors.weight_percentage ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 15"
                disabled={saving}
              />
              {errors.weight_percentage && (
                <p className="text-red-500 text-sm mt-1">{errors.weight_percentage}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#E8934D] to-[#E85D4D] text-white font-bold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-3 bg-gray-300 text-gray-800 font-bold rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
